import { useLazyQuery } from '@apollo/client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon, IconArrowRight } from '@flixbus/honeycomb-icons-react';
import { Autocomplete } from '@flixbus/honeycomb-react';

import { SEARCH_CITY_BY_NAME } from 'common/LocationSearch/qql/locationSearch';

import styles from './styles.module.css';

const ALL_LOCATIONS_DEFAULT_VALUE = 'all-locations';
const ALL_LOCATIONS_DEFAULT_LABEL = 'All locations';

const LocationMultiSearch = ({ fieldControl, setValue, label = 'Location', submitOnChange }) => {
	const [getCitiesByName, { loading, error, data }] = useLazyQuery(SEARCH_CITY_BY_NAME, {
		fetchPolicy: 'no-cache',
	});
	const [isDebounceTriggered, setDebounceTriggered] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const [isTyping, setIsTyping] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const isUserSelectionRef = useRef(false);
	const inputRef = useRef(null);

	const onDebounce = useCallback(
		(inputValue) => {
			if (
				inputValue &&
				typeof inputValue === 'string' &&
				inputValue.trim() !== '' &&
				inputValue.toLowerCase() !== ALL_LOCATIONS_DEFAULT_VALUE
			) {
				setHasInteracted(true);
				setIsTyping(true);
				getCitiesByName({ variables: { cityName: inputValue } });
				setDebounceTriggered(true);
				return undefined;
			}

			if (!inputValue || (typeof inputValue === 'string' && inputValue.trim() === '')) {
				setDebounceTriggered(false);
				setHasInteracted(false);
				setIsTyping(false);
				return;
			}

			return undefined;
		},
		[getCitiesByName]
	);

	const getData = useCallback(() => {
		if (!data?.searchCitiesByName) return [];
		const cities = data.searchCitiesByName.map((city) => ({
			title: city.name,
			value: city.id,
			key: city.id,
			timezone: city.timezone,
		}));
		return cities;
	}, [data?.searchCitiesByName]);

	const selectedValues = useMemo(() => {
		if (!Array.isArray(fieldControl.value)) return [];
		const allOptions = getData();

		return fieldControl.value
			.map((item) => {
				const itemValue = item.value || item.key || item.id;
				const matchingOption = allOptions.find(
					(opt) => opt.value === itemValue || opt.key === itemValue
				);
				if (matchingOption) {
					return matchingOption;
				}

				return {
					title: item.title || item.name,
					value: itemValue,
					key: itemValue,
				};
			})
			.filter(Boolean);
	}, [fieldControl.value, getData]);

	const options = useMemo(() => {
		if (error) return [];
		if (!isDebounceTriggered && !isExpanded) return [];
		if (!data?.searchCitiesByName) return [];
		return getData();
	}, [data, error, getData, isDebounceTriggered, isExpanded]);

	const shouldShowAllLocations = useMemo(() => {
		return selectedValues.length === 0 && !hasInteracted && !isExpanded && !isTyping && !isFocused;
	}, [selectedValues.length, hasInteracted, isExpanded, isTyping, isFocused]);

	const handleReset = useCallback(() => {
		if (selectedValues.length === 0) {
			return;
		}

		// Clear the value the same as when user removes all items manually
		setValue(fieldControl.name, [], { shouldDirty: true });
		fieldControl.onChange([]);

		// Clear state to show "All locations" - same as handleSelect when array is empty
		setDebounceTriggered(false);
		setHasInteracted(false);
		setIsExpanded(false);
		setIsTyping(false);
		setIsFocused(false);

		// Submit the form to update localStorage, the same as handleSelect
		if (submitOnChange) {
			setTimeout(() => {
				submitOnChange();
			}, 0);
		}
	}, [fieldControl, setValue, submitOnChange, selectedValues.length]);

	const handleSelect = useCallback(
		(selectedOptions) => {
			isUserSelectionRef.current = true;

			if (!Array.isArray(selectedOptions)) {
				isUserSelectionRef.current = false;
				return;
			}

			const filteredOptions = selectedOptions.filter(
				(option) =>
					option.value !== ALL_LOCATIONS_DEFAULT_VALUE && option.key !== ALL_LOCATIONS_DEFAULT_VALUE
			);

			const formattedOptions = filteredOptions.map((option) => ({
				...option,
				key: option.value || option.key,
			}));

			setValue(fieldControl.name, formattedOptions, { shouldDirty: true });
			fieldControl.onChange(formattedOptions);

			// Reset state when all items are removed
			if (formattedOptions.length === 0) {
				setHasInteracted(false);
				setIsExpanded(false);
				setIsTyping(false);
				setIsFocused(false);
			}

			setTimeout(() => {
				isUserSelectionRef.current = false;
			}, 100);

			if (submitOnChange) {
				setTimeout(() => {
					submitOnChange();
				}, 0);
			}
		},
		[fieldControl, setValue, submitOnChange]
	);

	// Watch when all options are removed or when values are set
	useEffect(() => {
		if (isUserSelectionRef.current) {
			return;
		}
		const currentValue = fieldControl.value;
		if (!Array.isArray(currentValue) || currentValue.length === 0) {
			setHasInteracted(false);
			setIsExpanded(false);
			setIsTyping(false);
			setIsFocused(false);
		} else {
			// If we have the values (including from localStorage) we mark them as interacted
			// This makes sure the restored values are displayed correctly
			setHasInteracted(true);
		}
	}, [fieldControl.value]);

	const handleToggle = useCallback(
		(expanded) => {
			setIsExpanded(expanded);
			if (expanded) {
				// When the user opens input - clear "All locations"
				setHasInteracted(true);
				setIsTyping(false);
				setIsFocused(true);

				// Set predefined list
				if (!data?.searchCitiesByName) {
					getCitiesByName({ variables: { cityName: '' } });
					setDebounceTriggered(true);
				}
			} else {
				// Reset state when all items are removed
				if (selectedValues.length === 0) {
					setDebounceTriggered(false);
					setHasInteracted(false);
					setIsTyping(false);
					setIsFocused(false);
				}
			}
		},
		[selectedValues.length, getCitiesByName, data]
	);

	const handleFocus = useCallback(() => {
		// Hide "All locations" on focus
		setIsFocused(true);
		setHasInteracted(true);

		// Set predefined list
		if (!data?.searchCitiesByName) {
			getCitiesByName({ variables: { cityName: '' } });
			setDebounceTriggered(true);
		}

		setIsExpanded(true);
	}, [getCitiesByName, data]);

	const handleBlur = useCallback(() => {
		// Reset state if no selections and set "All Locations" label
		if (selectedValues.length === 0 && !isExpanded) {
			setIsFocused(false);
			setHasInteracted(false);
			setIsTyping(false);
		}
	}, [selectedValues.length, isExpanded]);

	const handleKeyDown = useCallback(
		(e) => {
			// Prevent typing if "All locations" is present
			if (shouldShowAllLocations) {
				e.preventDefault();
				setHasInteracted(true);
				setIsFocused(true);
			}
		},
		[shouldShowAllLocations]
	);

	useEffect(() => {
		const input = document.getElementById('combo-timeline');
		if (input) {
			inputRef.current = input;

			// Remove focus if "All locations" is present
			if (shouldShowAllLocations && document.activeElement === input) {
				input.blur();
			}

			const handleInput = (e) => {
				// Prevent any input if "All locations" is set
				if (shouldShowAllLocations) {
					e.preventDefault();
					e.stopPropagation();
					e.target.value = ALL_LOCATIONS_DEFAULT_LABEL;
					setHasInteracted(true);
					setIsFocused(true);
				}
			};

			const handleBeforeInput = (e) => {
				if (shouldShowAllLocations) {
					e.preventDefault();
				}
			};

			input.addEventListener('input', handleInput);
			input.addEventListener('beforeinput', handleBeforeInput);

			return () => {
				input.removeEventListener('input', handleInput);
				input.removeEventListener('beforeinput', handleBeforeInput);
			};
		}
	}, [shouldShowAllLocations]);

	const autocompleteProps = {
		multiple: true,
		options,
		onChange: handleSelect,
		onToggle: handleToggle,
		extraClasses: styles.autocomplete,
		// Show selected values if exist or default "All locations" label
		...(selectedValues.length > 0 ? { value: selectedValues } : {}),
	};

	// Set default value
	if (shouldShowAllLocations) {
		autocompleteProps.inputValue = ALL_LOCATIONS_DEFAULT_LABEL;
	}

	const wrapperClasses = shouldShowAllLocations
		? `${styles.wrapper} ${styles.showingAllLocations}`
		: styles.wrapper;

	return (
		<div className={wrapperClasses}>
			<Autocomplete
				{...autocompleteProps}
				comboboxProps={{
					id: 'combo-timeline',
					label,
					loading,
					limitSelectedOptions: 2,
					resetProps: {
						label: 'Clear',
						onClick: handleReset,
					},
					toggleProps: {
						label: 'Toggle',
						children: <Icon InlineIcon={IconArrowRight} />,
					},
					onFocus: handleFocus,
					onBlur: handleBlur,
					onKeyDown: handleKeyDown,
				}}
				listBoxProps={{
					optionsToDisplay: 5,
					extraClasses: styles.listBox,
				}}
				onDebounce={onDebounce}
			/>
		</div>
	);
};

export default LocationMultiSearch;
